import { Logger } from "shared/lib/Logger"

import I18n from "i18n"
import { z } from "zod"
import { de as deLocale, en as enLocale } from "zod/locales"

const AVAILABLE_LOCALES: Record<string, typeof enLocale> = {
  de: deLocale,
  en: enLocale
}

try {
  // Normalize the language code (e.g., 'en-US' -> 'en', 'de-DE' -> 'de')
  const normalizedCode = I18n.language.split("-")[0].toLowerCase()

  const localeFunction =
    AVAILABLE_LOCALES[normalizedCode] || AVAILABLE_LOCALES.en

  z.config(localeFunction())
} catch (error) {
  // Silently fail and use default zod error messages
  if (error instanceof Error) {
    Logger.error(error, "failed to load zod locale")
  } else {
    Logger.warn("ZodError", "failed to load zod locale")
  }
}

z.config({
  customError: iss => {
    switch (iss.code) {
      case "invalid_type":
        if (iss.input === undefined || iss.input === null) {
          return {
            message: I18n.t("live:Validation.required")
          }
        }
        if (iss.expected === "string") {
          return {
            message: I18n.t("live:Validation.required")
          }
        }
        if (iss.expected === "number") {
          return {
            message: I18n.t("live:Validation.invalidNumber")
          }
        }
        if (iss.expected === "date") {
          return {
            message: I18n.t("live:Validation.invalidDate")
          }
        }
        if (iss.expected === "boolean") {
          return {
            message: I18n.t("live:Validation.invalidBoolean")
          }
        }
        return {
          message: I18n.t("live:Validation.invalidType", {
            expected: iss.expected,
            received: iss.input
          })
        }

      case "too_small":
        if (iss.origin === "string") {
          return {
            message: I18n.t("live:Validation.minLength", {
              minLength: iss.minimum
            })
          }
        }
        if (iss.origin === "number") {
          return {
            message: I18n.t("live:Validation.min", {
              min: iss.minimum
            })
          }
        }
        if (iss.origin === "array") {
          return {
            message: I18n.t("live:Validation.minItems", {
              minItems: iss.minimum
            })
          }
        }
        if (
          iss.origin === "date" &&
          (typeof iss.minimum === "number" || typeof iss.minimum === "string")
        ) {
          return {
            message: I18n.t("live:Validation.minDate", {
              minDate: new Date(iss.minimum).toLocaleDateString()
            })
          }
        }
        return undefined

      case "too_big":
        if (iss.origin === "string") {
          return {
            message: I18n.t("live:Validation.maxLength", {
              maxLength: iss.maximum
            })
          }
        }
        if (iss.origin === "number") {
          return {
            message: I18n.t("live:Validation.max", {
              max: iss.maximum
            })
          }
        }
        if (iss.origin === "array") {
          return {
            message: I18n.t("live:Validation.maxItems", {
              maxItems: iss.maximum
            })
          }
        }
        if (
          iss.origin === "date" &&
          (typeof iss.maximum === "number" || typeof iss.maximum === "string")
        ) {
          return {
            message: I18n.t("live:Validation.maxDate", {
              maxDate: new Date(iss.maximum).toLocaleDateString()
            })
          }
        }
        return undefined

      case "invalid_format":
        if (iss.validation === "email") {
          return {
            message: I18n.t("live:Validation.email")
          }
        }
        if (iss.validation === "url") {
          return {
            message: I18n.t("live:Validation.url")
          }
        }
        if (iss.validation === "regex") {
          return {
            message: I18n.t("live:Validation.format", {
              format: iss.validation
            })
          }
        }
        return {
          message: I18n.t("live:Validation.invalidFormat")
        }

      case "invalid_value":
        return {
          message: I18n.t("live:Validation.oneOf", {
            fieldValues: "" // iss.values(", ") || ""
          })
        }

      case "invalid_union":
        return {
          message: I18n.t("live:Validation.invalidUnion")
        }

      case "custom":
        return {
          message: iss.message || I18n.t("live:Validation.custom")
        }

      default:
        return undefined
    }
  }
})
